import domReady from '@wordpress/dom-ready';
import { __ } from '@wordpress/i18n';

const INIT_STATE = {
	clip: 'clip',
	poster: 'poster',
	full: 'full',
};

const ACTIVATE_KEY_CODES = [ 'Enter', ' ', 'Spacebar' ];

function ensureInteractiveElement( element, label ) {
	if ( ! element ) {
		return;
	}

	if ( element.tagName === 'BUTTON' ) {
		element.type = 'button';
		if ( ! element.hasAttribute( 'aria-label' ) ) {
			element.setAttribute( 'aria-label', label );
		}
		return;
	}

	element.setAttribute( 'role', 'button' );
	element.setAttribute( 'tabindex', '0' );

	if ( ! element.hasAttribute( 'aria-label' ) ) {
		element.setAttribute( 'aria-label', label );
	}
}

function bindFrame( frame ) {
	const clip = frame.querySelector( '[data-fmv-role="clip"]' );
	const full = frame.querySelector( '[data-fmv-role="full"]' );
	const toggle = frame.querySelector( '[data-fmv-role="toggle"]' );
	const poster = frame.querySelector( '[data-fmv-role="poster"]' );

	if ( ! full ) {
		return;
	}

	const label = __( 'Watch full video', 'featured-media' );
	const hasClip = !! clip;
	const hasPoster = !! poster;

	const interactiveTarget = toggle || poster || clip || frame;
	ensureInteractiveElement( interactiveTarget, label );

	const ensureInitialState = () => {
		if ( ! frame.dataset.fmvState ) {
			frame.dataset.fmvState = hasClip ? INIT_STATE.clip : ( hasPoster ? INIT_STATE.poster : INIT_STATE.full );
		}
		return frame.dataset.fmvState;
	};

	let state = ensureInitialState();

	if ( clip && state === INIT_STATE.clip ) {
		clip.muted = true;
		if ( ! clip.hasAttribute( 'muted' ) ) {
			clip.setAttribute( 'muted', 'muted' );
		}
		if ( clip.dataset.fmvAutoplay === '1' ) {
			clip.autoplay = true;
			if ( ! clip.hasAttribute( 'autoplay' ) ) {
				clip.setAttribute( 'autoplay', 'autoplay' );
			}
			if ( typeof clip.play === 'function' ) {
				const playPromise = clip.play();
				if ( playPromise && typeof playPromise.catch === 'function' ) {
					playPromise.catch( () => {
						// Autoplay might be blocked; ignore silently.
					} );
				}
			}
		}
	}

	const getState = () => frame.dataset.fmvState || state || INIT_STATE.full;

      const isThreeJsScene =
      typeof document !== 'undefined' &&
      document.body &&
      document.body.classList &&
      document.body.classList.contains('has-three-js-scene');

	const ensureFullVideoSrc = () => {
		if ( ! full ) {
			return;
		}
		// On pages with a Three.js scene, if a clip player is already initialized,
		// skip loading the full video to avoid unnecessary bandwidth.
		if ( isThreeJsScene && clip && clip.dataset.plyrInitialized === 'true' ) {
			return;
		}


		// If src is already set (e.g. initial state is full), do nothing.
		if ( full.getAttribute( 'src' ) ) {
			return;
		}

		const dataSrc = full.dataset.fmvSrc;
		if ( dataSrc ) {
			full.setAttribute( 'src', dataSrc );

			// Once we add src, hint the browser to start loading.
			if ( typeof full.load === 'function' ) {
				full.load();
			}
		}
	};

	const revealFullVideo = () => {
		if ( getState() === INIT_STATE.full ) {
			return;
		}

		// Ensure the full video src is only set when we actually reveal it.
		ensureFullVideoSrc();

		state = INIT_STATE.full;
		frame.dataset.fmvState = INIT_STATE.full;
		frame.classList.add( 'is-full-visible' );

		if ( toggle ) {
			toggle.setAttribute( 'aria-pressed', 'true' );
			toggle.setAttribute( 'hidden', 'hidden' );
			toggle.setAttribute( 'aria-hidden', 'true' );
		}

		if ( clip ) {
			try {
				clip.pause();
			} catch ( error ) {
				// Ignore pause errors – e.g. autoplay restrictions.
			}
			clip.setAttribute( 'hidden', 'hidden' );
			clip.setAttribute( 'aria-hidden', 'true' );
		}

		if ( poster ) {
			poster.setAttribute( 'hidden', 'hidden' );
			poster.setAttribute( 'aria-hidden', 'true' );
		}

		full.removeAttribute( 'hidden' );
		full.setAttribute( 'aria-hidden', 'false' );

		if ( ( hasClip || hasPoster ) && ! full.hasAttribute( 'controls' ) ) {
			full.setAttribute( 'controls', 'controls' );
		}

		if ( hasClip ) {
			full.muted = false;
		}

		if ( typeof full.play === 'function' ) {
			const playPromise = full.play();
			if ( playPromise && typeof playPromise.catch === 'function' ) {
				playPromise.catch( () => {
					// Silently swallow autoplay failures.
				} );
			}
		}

		if ( ! full.hasAttribute( 'tabindex' ) ) {
			full.setAttribute( 'tabindex', '-1' );
		}

		if ( typeof full.focus === 'function' ) {
			full.focus( { preventScroll: true } );
		}
	};

	const onClick = ( event ) => {
		event.preventDefault();
		revealFullVideo();
	};

	const onKeyDown = ( event ) => {
		if ( ACTIVATE_KEY_CODES.includes( event.key ) ) {
			event.preventDefault();
			revealFullVideo();
		}
	};

	if ( interactiveTarget ) {
		interactiveTarget.addEventListener( 'keydown', onKeyDown );
	}

	const clickTargets = new Set( [ frame ] );

	if ( toggle ) {
		clickTargets.add( toggle );
	}

	if ( poster ) {
		clickTargets.add( poster );
	} else if ( clip ) {
		clickTargets.add( clip );
	}

	clickTargets.forEach( ( element ) => {
		element.addEventListener( 'click', onClick );
	} );
}

domReady( () => {
	const frames = document.querySelectorAll( '.featured-video-block .featured-video__frame[data-fmv-has-full="1"]' );

	frames.forEach( ( frame ) => {
		if ( frame.dataset.fmvBound === '1' ) {
			return;
		}

		frame.dataset.fmvBound = '1';
		bindFrame( frame );
	});
});
